"use client";

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { connectWallet } from "@/lib/wallet/sign";
import { WalletError, type DetectedWallet, type WalletChain } from "@/lib/wallet/types";
import { useWallets as useBrowserWallets } from "@/lib/wallet/use-wallets";
import { shortenAddress } from "@/lib/format/amount";
import { Button } from "@/components/ui/primitives";
import { Note } from "./fields";

/* The Web3 Wallet section from the kit's deposit frame, shared by deposit and
   withdrawal: a Connect Wallet button while nothing is connected, and once
   something is, the wallet's own icon, its shortened address and a Disconnect.

   The whitelabel keeps one connection for the whole wallet hub (its AppKit
   modal); this is the same shape without the SDK — the EIP-6963 wallets the
   browser announced, one chosen connection held at the modal level so deposit
   and withdrawal see the same wallet, and "disconnect" simply forgets it,
   which is all an injected wallet offers. */

export type Web3Connection = {
  wallet: DetectedWallet;
  address: string;
  chainId?: number;
};

const Web3WalletContext = createContext<{
  connection: Web3Connection | null;
  connect: (wallet: DetectedWallet) => Promise<void>;
  disconnect: () => void;
} | null>(null);

export const useWeb3Wallet = () => {
  const context = useContext(Web3WalletContext);

  if (!context) {
    throw new Error("useWeb3Wallet must be used inside Web3WalletProvider");
  }

  return context;
};

export function Web3WalletProvider({ children }: { children: ReactNode }) {
  const [connection, setConnection] = useState<Web3Connection | null>(null);

  const value = useMemo(
    () => ({
      connection,
      connect: async (wallet: DetectedWallet) => {
        const result = await connectWallet(wallet);
        setConnection({ wallet, address: result.address, chainId: result.chainId });
      },
      disconnect: () => setConnection(null),
    }),
    [connection],
  );

  return <Web3WalletContext.Provider value={value}>{children}</Web3WalletContext.Provider>;
}

export function Web3WalletCard({ chain }: { chain: WalletChain }) {
  const { connection, connect, disconnect } = useWeb3Wallet();
  const wallets = (useBrowserWallets(true) ?? []).filter((wallet) => wallet.chain === chain);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const chosen = wallets.find((wallet) => wallet.id === selectedId) ?? wallets.at(0) ?? null;

  if (connection) {
    return (
      <div className="mb-[4px] flex items-center gap-[10px] rounded-lg border border-border-input bg-elevation-2 p-[10px]">
        {connection.wallet.icon ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={connection.wallet.icon} alt="" className="h-[26px] w-[26px] shrink-0 rounded-md" />
        ) : (
          <span className="h-[26px] w-[26px] shrink-0 rounded-md bg-elevation-3" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12px] font-bold leading-[1.5] text-fg">
            {shortenAddress(connection.address)}
          </span>
          <span className="block text-[9px] leading-[1.5] text-fg-subtle">{connection.wallet.name}</span>
        </span>
        <button
          type="button"
          onClick={disconnect}
          className="shrink-0 text-[12px] font-bold leading-[1.6] text-fg-subtle transition-colors hover:text-fg"
        >
          Disconnect
        </button>
      </div>
    );
  }

  if (wallets.length === 0) {
    return (
      <Note tone="muted">
        No browser wallet detected. Install one, or send from any wallet to the address above — the
        deposit is credited automatically either way.
      </Note>
    );
  }

  const start = async () => {
    if (!chosen) return;

    setConnecting(true);
    setFailure(null);

    try {
      await connect(chosen);
    } catch (error) {
      setFailure(
        error instanceof WalletError ? error.message : "The wallet did not connect. Try again.",
      );
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="mb-[4px] flex flex-col gap-[8px]">
      <div className="flex items-center gap-[8px]">
        {wallets.length > 1 && (
          <select
            aria-label="Wallet to connect"
            value={chosen?.id ?? ""}
            onChange={(event) => setSelectedId(event.target.value)}
            className="h-[38px] flex-1 rounded-lg border border-border-input bg-elevation-2 px-[8px] text-[12px] font-bold leading-[1.6] text-fg-subtle outline-none"
          >
            {wallets.map((wallet) => (
              <option key={wallet.id} value={wallet.id}>
                {wallet.name}
              </option>
            ))}
          </select>
        )}
        <Button onClick={() => void start()} disabled={connecting} className="flex-1">
          {connecting
            ? "Confirm in your wallet…"
            : `Connect ${wallets.length === 1 ? chosen?.name : "Wallet"}`}
        </Button>
      </div>

      {failure && <Note tone="error">{failure}</Note>}
    </div>
  );
}
